import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { useRest } from "@/composables/index";
import { invalidateSifcopDetailCache } from "@/utils/sifcopDetailCache";
import {
  datosGeneralesFields,
  personaFields,
  dynamicSections,
  validateSifcopValue,
} from "@/utils/sifcopValidation";

const buildSifcopEndpoint = () => {
  const base = String(process.env.VUE_APP_BACKEND_ENVIRONMENT || "").replace(/\/$/, "");
  const sifcopBase = base.replace(/\/api\/v1(?=\/|$)/i, "/api/v3");
  return `${sifcopBase}/sifcop`;
};

const SIFCOP_ENDPOINT = buildSifcopEndpoint();

const emptyDatosGeneralesActa = () => ({
  organismoInterviniente: "",
  dependenciaPolicial: "",
  lugar: "",
  fecha: "",
  departamento: "",
  provincia: "",
  hora: "",
  dia: "",
  mes: "",
  anio: "",
  funcionarioPolicialSuscribe: "",
  usuarioSifcop: "",
  numeroRequerimientoJudicial: "",
});

const emptyPersonaRegistrada = () => ({
  nombreApellido: "",
  documento: "",
  fechaNacimiento: "",
  nacionalidad: "",
  domicilio: "",
});

const emptyVehiculo = () => ({
  dominio: "",
  anio: "",
  tipoVehiculo: "",
  marca: "",
  numeroMotor: "",
  numeroChasis: "",
  modelo: "",
  color: "",
});

const emptyArma = () => ({
  tipoArma: "",
  marca: "",
  modelo: "",
  calibre: "",
  numeroSerie: "",
});

const emptyElemento = () => ({
  tipoElemento: "",
  identificacion: "",
  descripcion: "",
  modelo: "",
});

const emptyForm = () => ({
  SifcopID: null,
  datosGeneralesActa: emptyDatosGeneralesActa(),
  personaRegistrada: emptyPersonaRegistrada(),
  vehiculos: [emptyVehiculo()],
  armas: [emptyArma()],
  elementos: [emptyElemento()],
});

const clone = (value) => JSON.parse(JSON.stringify(value));

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

const ensureAtLeastOne = (items, factory) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [factory()];
  }

  return items.map((item) => ({
    ...factory(),
    ...(item || {}),
  }));
};

const normalizeStoredDate = (value) => {
  if (!value) return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed;
};

const formatDateToIso = (value) => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const ddmmyyyy = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const [, day, month, year] = ddmmyyyy;
      return `${year}-${month}-${day}`;
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = `${parsed.getMonth() + 1}`.padStart(2, "0");
      const day = `${parsed.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  }

  return null;
};

const normalizeHora = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  if (/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(trimmed)) {
    return trimmed;
  }

  return trimmed;
};

const mapUsuarioSifcop = (value) => {
  if (value === true || value === 1 || value === "1") return "1";
  if (value === false || value === 0 || value === "0") return "0";
  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    if (normalized === "SI") return "1";
    if (normalized === "NO") return "0";
  }
  return null;
};

const hasAnyValue = (item = {}) =>
  Object.values(item || {}).some((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });

const mapSifcopPayload = (form = {}) => {
  const datosGenerales = form?.datosGeneralesActa || {};
  const persona = form?.personaRegistrada || {};

  return {
    acta: {
      OrganismoInterviniente: datosGenerales.organismoInterviniente || "",
      DependenciaPolicial: datosGenerales.dependenciaPolicial || "",
      Lugar: datosGenerales.lugar || "",
      Fecha: formatDateToIso(datosGenerales.fecha),
      Departamento: datosGenerales.departamento || "",
      Provincia: datosGenerales.provincia || "",
      Hora: normalizeHora(datosGenerales.hora),
      Dia: datosGenerales.dia || "",
      Mes: datosGenerales.mes || "",
      Anio: datosGenerales.anio || "",
      FuncionarioPolicialQueSuscribe: datosGenerales.funcionarioPolicialSuscribe || "",
      UsuarioSIFCOP: mapUsuarioSifcop(datosGenerales.usuarioSifcop),
      NumeroRequerimientoJudicial: datosGenerales.numeroRequerimientoJudicial || "",
      NombreApellido: persona.nombreApellido || "",
      Documento: persona.documento || "",
      FechaNacimiento: formatDateToIso(persona.fechaNacimiento),
      Nacionalidad: persona.nacionalidad || "",
      Domicilio: persona.domicilio || "",
    },
    vehiculos: (form?.vehiculos || [])
      .filter((item) => hasAnyValue(item))
      .map((item) => ({
        Dominio: item.dominio || "",
        Anio: item.anio || "",
        TipoVehiculo: item.tipoVehiculo || "",
        Marca: item.marca || "",
        NumeroMotor: item.numeroMotor || "",
        NumeroChasis: item.numeroChasis || "",
        Modelo: item.modelo || "",
        Color: item.color || "",
      })),
    armas: (form?.armas || [])
      .filter((item) => hasAnyValue(item))
      .map((item) => ({
        TipoArma: item.tipoArma || "",
        Marca: item.marca || "",
        Modelo: item.modelo || "",
        Calibre: item.calibre || "",
        NumeroSerie: item.numeroSerie || "",
      })),
    elementos: (form?.elementos || [])
      .filter((item) => hasAnyValue(item))
      .map((item) => ({
        TipoElemento: item.tipoElemento || "",
        Identificacion: item.identificacion || "",
        Descripcion: item.descripcion || "",
        Modelo: item.modelo || "",
      })),
  };
};

const formatIsoToDmy = (value) => {
  if (typeof value !== "string") return value || "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return trimmed;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

export const mapBackendDocToForm = (doc = {}) => {
  const raw = doc?.acta || {};
  return {
    SifcopID: raw.ActaSifcopID || doc?.id || null,
    datosGeneralesActa: {
      ...emptyDatosGeneralesActa(),
      organismoInterviniente: raw.OrganismoInterviniente || "",
      dependenciaPolicial: raw.DependenciaPolicial || "",
      lugar: raw.Lugar || "",
      fecha: normalizeStoredDate(raw.Fecha),
      departamento: raw.Departamento || "",
      provincia: raw.Provincia || "",
      hora: raw.Hora || "",
      dia: raw.Dia || "",
      mes: raw.Mes || "",
      anio: raw.Anio || "",
      funcionarioPolicialSuscribe: raw.FuncionarioPolicialQueSuscribe || "",
      usuarioSifcop: raw.UsuarioSIFCOP === 1 || raw.UsuarioSIFCOP === "1" ? "SI" : "NO",
      numeroRequerimientoJudicial: raw.NumeroRequerimientoJudicial || "",
    },
    personaRegistrada: {
      ...emptyPersonaRegistrada(),
      nombreApellido: raw.NombreApellido || "",
      documento: raw.Documento || "",
      fechaNacimiento: formatIsoToDmy(raw.FechaNacimiento),
      nacionalidad: raw.Nacionalidad || "",
      domicilio: raw.Domicilio || "",
    },
    vehiculos: ensureAtLeastOne(
      (doc?.vehiculos || []).map((item) => ({
        dominio: item.Dominio || "",
        anio: item.Anio || "",
        tipoVehiculo: item.TipoVehiculo || "",
        marca: item.Marca || "",
        numeroMotor: item.NumeroMotor || "",
        numeroChasis: item.NumeroChasis || "",
        modelo: item.Modelo || "",
        color: item.Color || "",
      })),
      emptyVehiculo
    ),
    armas: ensureAtLeastOne(
      (doc?.armas || []).map((item) => ({
        tipoArma: item.TipoArma || "",
        marca: item.Marca || "",
        modelo: item.Modelo || "",
        calibre: item.Calibre || "",
        numeroSerie: item.NumeroSerie || "",
      })),
      emptyArma
    ),
    elementos: ensureAtLeastOne(
      (doc?.elementos || []).map((item) => ({
        tipoElemento: item.TipoElemento || "",
        identificacion: item.Identificacion || "",
        descripcion: item.Descripcion || "",
        modelo: item.Modelo || "",
      })),
      emptyElemento
    ),
  };
};

export const useSifcopStore = defineStore("sifcop", () => {
  const rest = useRest();
  const selected = ref(null);
  const form = ref(emptyForm());
  const formType = ref("view");
  const sifcopList = ref([]);
  const isLoading = ref(false);
  const isFormValid = ref(false);
  const validationErrors = ref({});

  const validateForm = () => {
    const errors = {};

    datosGeneralesFields.forEach((field) => {
      const error = validateSifcopValue(
        "datosGeneralesActa",
        field.key,
        form.value?.datosGeneralesActa?.[field.key]
      );

      if (error) {
        errors[`datosGeneralesActa.${field.key}`] = error;
      }
    });

    personaFields.forEach((field) => {
      const error = validateSifcopValue(
        "personaRegistrada",
        field.key,
        form.value?.personaRegistrada?.[field.key]
      );

      if (error) {
        errors[`personaRegistrada.${field.key}`] = error;
      }
    });

    dynamicSections.forEach((section) => {
      const items = form.value?.[section.key] || [];

      items.forEach((item, index) => {
        section.fields.forEach((field) => {
          const error = validateSifcopValue(section.key, field.key, item?.[field.key]);

          if (error) {
            errors[`${section.key}.${index}.${field.key}`] = error;
          }
        });
      });
    });

    validationErrors.value = errors;
    isFormValid.value = Object.keys(errors).length === 0;

    return isFormValid.value;
  };

  const addVehiculo = () => {
    form.value.vehiculos.push(emptyVehiculo());
  };

  const addArma = () => {
    form.value.armas.push(emptyArma());
  };

  const addElemento = () => {
    form.value.elementos.push(emptyElemento());
  };

  const removeVehiculo = (index) => {
    if (form.value.vehiculos.length === 1) {
      form.value.vehiculos[0] = emptyVehiculo();
      return;
    }

    form.value.vehiculos = form.value.vehiculos.filter((_, i) => i !== index);
  };

  const removeArma = (index) => {
    if (form.value.armas.length === 1) {
      form.value.armas[0] = emptyArma();
      return;
    }

    form.value.armas = form.value.armas.filter((_, i) => i !== index);
  };

  const removeElemento = (index) => {
    if (form.value.elementos.length === 1) {
      form.value.elementos[0] = emptyElemento();
      return;
    }

    form.value.elementos = form.value.elementos.filter((_, i) => i !== index);
  };

  const updateDatosGeneralesField = (field, value) => {
    form.value.datosGeneralesActa[field] = value;
  };

  const updatePersonaField = (field, value) => {
    form.value.personaRegistrada[field] = value;
  };

  const updateVehiculoField = (index, field, value) => {
    if (!form.value.vehiculos[index]) return;
    form.value.vehiculos[index][field] = value;
  };

  const updateArmaField = (index, field, value) => {
    if (!form.value.armas[index]) return;
    form.value.armas[index][field] = value;
  };

  const updateElementoField = (index, field, value) => {
    if (!form.value.elementos[index]) return;
    form.value.elementos[index][field] = value;
  };

  const hydrateFormFromDoc = (doc) => {
    form.value = {
      SifcopID: doc?.SifcopID || doc?.id || null,
      datosGeneralesActa: {
        ...emptyDatosGeneralesActa(),
        ...(doc?.datosGeneralesActa || {}),
      },
      personaRegistrada: {
        ...emptyPersonaRegistrada(),
        ...(doc?.personaRegistrada || {}),
      },
      vehiculos: ensureAtLeastOne(doc?.vehiculos, emptyVehiculo),
      armas: ensureAtLeastOne(doc?.armas, emptyArma),
      elementos: ensureAtLeastOne(doc?.elementos, emptyElemento),
    };

    form.value.datosGeneralesActa.fecha = normalizeStoredDate(form.value.datosGeneralesActa.fecha);
  };

  const select = (doc) => {
    selected.value = doc;
    hydrateFormFromDoc(doc);
    formType.value = "view";
  };

  const hydrateFormFromBackendDoc = (doc) => {
    form.value = mapBackendDocToForm(doc);
  };

  const selectFirst = () => {
    if (sifcopList.value?.length) {
      select(sifcopList.value[0]);
    }
  };

  const selectNextOrPrevious = (indexToRedirect) => {
    if (!sifcopList.value.length) return;

    if (sifcopList.value[indexToRedirect] === undefined) {
      select(sifcopList.value[indexToRedirect - 1]);
      return;
    }

    select(sifcopList.value[indexToRedirect]);
  };

  const startCreate = () => {
    selected.value = null;
    form.value = emptyForm();
    formType.value = "create";
  };

  const startEdit = () => {
    formType.value = "edit";
  };

  const startView = () => {
    formType.value = "view";
  };

  const resetForm = () => {
    form.value = emptyForm();
  };

  const clear = () => {
    selected.value = null;
    resetForm();
  };

  const setSifcopList = (records = []) => {
    sifcopList.value = Array.isArray(records) ? clone(records) : [];
    if (!sifcopList.value.length) {
      clear();
      return;
    }

    selectFirst();
  };

  const handleDeleteSifcop = async () => {
    const id = selectedId.value;
    if (!id) return false;

    isLoading.value = true;

    try {
      const selectedIndexArray = sifcopList.value.findIndex(
        (item) => item.SifcopID === id
      );

      sifcopList.value = sifcopList.value.filter((item) => item.SifcopID !== id);

      if (!sifcopList.value.length) {
        clear();
      } else {
        selectNextOrPrevious(selectedIndexArray);
      }

      return true;
    } catch (error) {
      console.error("Error al eliminar registro SIFCOP", error);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) return { ok: false };

    isLoading.value = true;

    try {
      const payload = mapSifcopPayload(form.value);
      await rest.post({
        url: SIFCOP_ENDPOINT,
        formData: payload,
      });
      return { ok: true };
    } catch (error) {
      console.error("Error al crear registro SIFCOP", error);
      return { ok: false };
    } finally {
      isLoading.value = false;
    }
  };

  const handleUpdate = async (id) => {
    const targetId = id || form.value?.SifcopID;
    if (!targetId || !validateForm()) return { ok: false };

    isLoading.value = true;
    try {
      const payload = mapSifcopPayload(form.value);
      await rest.put({
        url: `${SIFCOP_ENDPOINT}/${targetId}`,
        formData: payload,
      });
      invalidateSifcopDetailCache(targetId);
      return { ok: true };
    } catch (error) {
      console.error("Error al actualizar registro SIFCOP", error);
      return { ok: false };
    } finally {
      isLoading.value = false;
    }
  };

  const setValidationErrors = (errors) => {
    validationErrors.value = errors || {};
  };

  const selectedId = computed(() => selected.value?.SifcopID ?? null);
  const isSelected = (id) => selected.value?.SifcopID === id;
  const isView = computed(() => formType.value === "view");
  const isEdit = computed(() => formType.value === "edit");
  const isCreate = computed(() => formType.value === "create");
  const inputsDisabled = computed(() => formType.value === "view");

  return {
    // state
    selected,
    form,
    formType,
    sifcopList,
    isLoading,
    isFormValid,
    validationErrors,

    // actions
    select,
    selectFirst,
    selectNextOrPrevious,
    hydrateFormFromDoc,
    hydrateFormFromBackendDoc,
    startCreate,
    startEdit,
    startView,
    resetForm,
    clear,
    validateForm,
    handleCreate,
    handleUpdate,
    handleDeleteSifcop,
    setSifcopList,
    setValidationErrors,
    addVehiculo,
    addArma,
    addElemento,
    removeVehiculo,
    removeArma,
    removeElemento,
    updateDatosGeneralesField,
    updatePersonaField,
    updateVehiculoField,
    updateArmaField,
    updateElementoField,

    // getters
    selectedId,
    isSelected,
    isView,
    isEdit,
    isCreate,
    inputsDisabled,
  };
});
